import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Bell, Check, Loader2 } from 'lucide-react';
import { useNotifications } from '@/shared/hooks/useNotifications/useNotifications';
import type { NotificationButtonProps } from './NotificationButton.types';

const formatRelativeTime = (dateString: string | null): string => {
  if (!dateString) return '';
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

export const NotificationButton = ({ refreshInterval }: NotificationButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { unreadCount, notifications, isLoading, markAsRead, markAllAsRead } = useNotifications(
    refreshInterval,
    isOpen
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleNotificationClick = async (notification: (typeof notifications)[0]) => {
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }
    const url = notification.data?.url;
    if (typeof url === 'string' && url.startsWith('/') && !url.startsWith('//')) {
      navigate(url);
      setIsOpen(false);
    }
  };

  return (
    <div className='relative' ref={dropdownRef}>
      <button
        type='button'
        aria-label='Notifications'
        aria-haspopup='true'
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        className={clsx(
          'relative flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-all duration-200 hover:cursor-pointer hover:bg-gray-100 hover:text-gray-700',
          isOpen && 'bg-gray-100 text-gray-700'
        )}
      >
        <Bell className='h-5 w-5' />
        {unreadCount > 0 && (
          <span className='absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white'>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          role='menu'
          className='absolute right-0 z-50 mt-2 w-80 origin-top-right rounded-lg border border-gray-200 bg-white shadow-lg sm:w-96'
        >
          {/* Header */}
          <div className='flex items-center justify-between border-b border-gray-100 px-4 py-3'>
            <h3 className='text-sm font-semibold text-gray-900'>Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className='flex items-center gap-1 text-xs text-blue-600 hover:cursor-pointer hover:text-blue-700'
              >
                <Check className='h-3 w-3' />
                Mark all as read
              </button>
            )}
          </div>

          {/* Notification list */}
          <div className='max-h-80 overflow-y-auto'>
            {isLoading ? (
              <div className='flex items-center justify-center py-8'>
                <Loader2 className='h-5 w-5 animate-spin text-gray-400' />
              </div>
            ) : notifications.length === 0 ? (
              <div className='py-8 text-center'>
                <Bell className='mx-auto h-8 w-8 text-gray-300' />
                <p className='mt-2 text-sm text-gray-500'>No notifications yet</p>
              </div>
            ) : (
              notifications.map(notification => (
                <button
                  key={notification.id}
                  role='menuitem'
                  onClick={() => handleNotificationClick(notification)}
                  className={clsx(
                    'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:cursor-pointer hover:bg-gray-50',
                    !notification.is_read && 'bg-blue-50/50'
                  )}
                >
                  {/* Unread indicator */}
                  <div className='mt-1.5 flex-shrink-0'>
                    <div
                      className={clsx(
                        'h-2 w-2 rounded-full',
                        notification.is_read ? 'bg-transparent' : 'bg-blue-500'
                      )}
                    />
                  </div>

                  {/* Content */}
                  <div className='min-w-0 flex-1'>
                    <p
                      className={clsx(
                        'truncate text-sm',
                        notification.is_read ? 'text-gray-700' : 'font-medium text-gray-900'
                      )}
                    >
                      {notification.title}
                    </p>
                    {notification.body && (
                      <p className='mt-0.5 line-clamp-2 text-xs text-gray-500'>
                        {notification.body}
                      </p>
                    )}
                    <p className='mt-1 text-xs text-gray-400'>
                      {formatRelativeTime(notification.created_at)}
                    </p>
                  </div>

                  {/* Priority indicator */}
                  {notification.priority === 'urgent' && (
                    <span className='mt-1 flex-shrink-0 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700'>
                      Urgent
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
